import type {
  CellRendererSelectorResult,
  ColDef,
  DragStoppedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  IRowNode,
  IServerSideDatasource,
  ModelUpdatedEvent,
  RowClassParams,
  RowGroupOpenedEvent
} from 'ag-grid-community';
import { CellErrorTypes, cellErrorTypeText } from './agGridComponents/cellErrorTypes';
import { FormattingModalProps } from '@opengov/reporting-app/application/accountSummary/MultiDataset';
import {
  ColumnValue,
  EGroupType,
  IAccountSummary,
  IColumnSource,
  IExpandedGroupKeys,
  IExpandedRows,
  IRowSource,
  MultiHierarchyAccountSummary,
  isMultiHierarchyAccountSummary
} from '@opengov/reporting-types';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { clearPivotRowAndColumnCache } from '../../api/reportingApi';
import { LocaleText } from '../../locale';
import { IColumnEditorNavigatorFilter } from '../editor/sidepanel/columnEditor/ColumnEditorNavigator';
import styles from './FinancialReport.module.scss';
import { AutoGroupHeaderComponent } from './agGridComponents/AutoGroupHeaderComponent';
import { DefaultColumnGroupHeader } from './agGridComponents/DefaultColumnGroupHeader';
import { DeletedGroupColumnHeader } from './agGridComponents/DeletedGroupColumnHeader';
import { MdsColumnHeader } from './agGridComponents/MdsColumnHeader';
import { MdsRowGroupHeader } from './agGridComponents/MdsRowGroupHeader';
import { ZeroRowsStatusBar, type ZeroRowsStatusBarSetter } from './agGridComponents/ZeroRowsStatusBar';
import { useZeroRowsStatusBar } from './agGridComponents/hooks/useZeroRowsStatusBar';
import {
  AG_GRID_DEFAULT_ROW_HEIGHT,
  AG_GRID_GROUP_COLUMN_ID,
  AG_GRID_MIN_LEFTMOST_COLUMN_WIDTH,
  AG_GRID_MIN_LEFTMOST_COLUMN_WIDTH_WITH_MH_TOTALS_CHECKBOX,
  AG_GRID_NEW_COLUMN_PLACEHOLDER_ID,
  AG_GRID_NEW_ROW_PLACEHOLDER_ID,
  ExpandAllRunState,
  MultiHierarchyShowTotalsAtBottomContext,
  selectNodeAndChildren
} from './agGridConsts';
import { Context } from './context';
import {
  applyColumnOrder,
  applyColumnsVisibility,
  areAllColumnGroupHeadersHidden,
  clearAffectedGroupOrders,
  CustomLoadingCell,
  getDefinitionOrderColIds,
  normalizeAgGridKey,
  NoRowsOverlay,
  PendingValueCell,
  reorderGroupInState
} from './gridUtils';

export interface GridOptionsHookProps {
  columnFilterProps: IColumnEditorNavigatorFilter;
  currentColumnOrder: Record<string, number>;
  currentColumnsHidden: Record<string, boolean>;
  dataTableError: boolean;
  expandedGroupKeys: IExpandedGroupKeys;
  expandCollapseOneLevelEnabled: boolean;
  /** When true, public MH viewers cannot change expansion (BUD-18770). */
  lockRowExpansion?: boolean;
  usesExpandedGroupKeys: boolean;
  expandedRows: IExpandedRows;
  isEditing: boolean;
  localeText: LocaleText['multidataset'];
  selectedRowGroup: IRowSource;
  serverSideDatasource: IServerSideDatasource;
  isRefreshRowData: boolean;
  /** With `isRefreshRowData`: purge the grid's rows but leave the pivot cache alone (columns just loaded). */
  isRefreshRowKeepingCache?: boolean;
  pivotColumns: ColumnValue[];
  /** Ids of the column groups `pivotColumns` was fetched for; null when unknown. */
  pivotColumnGroupIds?: string[] | null;
  report: IAccountSummary | MultiHierarchyAccountSummary;
  dataUpdatedAt: string;
  updatedAt: string;
  rollupDimensionDataUpdatedAt?: string;
  rowGroupDebounceMs: number;
  canEditReport: boolean;
  hasParentData: boolean;
  onClickEditReport(): void;
  reportConfigurationId: string;
  deletedRowGroupings?: Record<number, number[]>;
  columnGroups: IColumnSource[];
  columnDefinitions: ColDef[];
  multiHierarchyShowTotalsAtBottom?: MultiHierarchyShowTotalsAtBottomContext;
}

export interface GridEvents {
  onEditRowGroup(rowIndex: number, lensId: string, groupId: string | undefined, rowLabel: string): unknown;
  onModelUpdated(event: ModelUpdatedEvent): unknown;
  onRowGroupOpened(event: RowGroupOpenedEvent): unknown;
  onColumnOrderChange(columnOrder: Record<string, number>): unknown;
  onGridReady(params: GridReadyEvent): unknown;
  onExpandRow(params: { api: GridApi; node?: IRowNode }): unknown;
  onCollapseRow(params: { api: GridApi; node?: IRowNode }): unknown;
  onExpandAll(params: { api: GridApi }): unknown;
  onCollapseAll(params: { api: GridApi }): unknown;
  onColumnVisibilityChange(hiddenColumns: Record<string, boolean>): unknown;
}

export type Events = GridEvents & Context['events'];

type ExpandAllProps = {
  expandAllRunRef: React.MutableRefObject<ExpandAllRunState>;
};

interface GridContext {
  currentRowLensesId: string | null | undefined;
  dataUpdatedAt: string;
  expandedRows: IExpandedRows;
  expandedGroupKeys: IExpandedGroupKeys;
  usesExpandedGroupKeys: boolean;
  inEditMode: boolean;
  selectedRowGroupId: string | undefined;
  currentColumnOrder: Record<string, number>;
  currentColumnsHidden: Record<string, boolean>;
  dataTableError: boolean;
  report: IAccountSummary | MultiHierarchyAccountSummary;
  columnValues: ColumnValue[];
  columnValueGroupIds: string[] | null;
  canEditReport: boolean;
  reportConfigurationId: string;
  deletedRowGroupings: Record<number, number[]>;
  columnGroups: IColumnSource[];
  columnDefinitions: ColDef[];
  updatedAt: string;
  rollupDimensionDataUpdatedAt: string | undefined;
  multiHierarchyShowTotalsAtBottom: MultiHierarchyShowTotalsAtBottomContext | undefined;
  expandAllRunRef: React.MutableRefObject<ExpandAllRunState>;
  lockRowExpansion?: boolean;
}

function buildCountByPrefix(colIds: string[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const colId of colIds) {
    const prefix = colId.split('.')[0];
    if (prefix === colId) continue;
    counts.set(prefix, (counts.get(prefix) ?? 0) + 1);
  }
  return counts;
}

function applyReorderedColumns(
  api: GridApi,
  currentColumnOrder: Record<string, number>,
  defOrderIds: string[],
  prevCountByPrefix: Map<string, number>,
  events: Partial<Events>
): void {
  const cleanedOrder = clearAffectedGroupOrders(currentColumnOrder, defOrderIds, prevCountByPrefix);

  if (cleanedOrder === currentColumnOrder) {
    applyColumnOrder(api, currentColumnOrder, defOrderIds);
    return;
  }

  // Derive affected prefixes from count increases rather than key diffs so that
  // groups with no prior saved order (empty columnOrder) are still reordered.
  const currentCountByPrefix = buildCountByPrefix(defOrderIds);
  const affectedPrefixes = new Set<string>(
    [...currentCountByPrefix.entries()]
      .filter(([prefix, count]) => count > (prevCountByPrefix.get(prefix) ?? 0))
      .map(([prefix]) => prefix)
  );

  const columnState = api.getColumnState();
  if (!columnState) return;

  let currentState = columnState.map((col) => col.colId);
  for (const prefix of affectedPrefixes) {
    currentState = reorderGroupInState(currentState, prefix, defOrderIds);
  }

  const newColumnOrder: Record<string, number> = Object.fromEntries(
    currentState.map((colId, idx) => [normalizeAgGridKey(colId), idx])
  );
  api.applyColumnState({ state: currentState.map((colId) => ({ colId })), applyOrder: true });
  events.onColumnOrderChange?.(newColumnOrder);
}

export function useGridOptions(
  props: GridOptionsHookProps & FormattingModalProps & ExpandAllProps,
  events: Partial<Events> = {}
) {
  const {
    columnFilterProps,
    currentColumnOrder,
    currentColumnsHidden,
    dataTableError,
    dataUpdatedAt,
    expandedGroupKeys,
    expandCollapseOneLevelEnabled,
    expandAllRunRef,
    usesExpandedGroupKeys,
    expandedRows,
    isEditing: inEditMode,
    localeText,
    selectedRowGroup,
    serverSideDatasource,
    isRefreshRowData,
    isRefreshRowKeepingCache,
    canEditReport,
    hasParentData,
    onClickEditReport,
    setShowFormattingModal,
    reportConfigurationId,
    columnGroups,
    columnDefinitions,
    updatedAt,
    rollupDimensionDataUpdatedAt,
    multiHierarchyShowTotalsAtBottom
  } = props;
  const lockRowExpansion = Boolean(props.lockRowExpansion);
  const currentGridContext = useRef<GridContext | null>(null);
  const columnMoved = useRef(false);
  const prevCountByPrefixRef = useRef<Map<string, number> | null>(null);
  const [gridAPI, setGridAPI] = useState<GridApi | null>(null);
  const zeroRowsSetterRef = useRef<ZeroRowsStatusBarSetter | null>(null);
  // Kept current synchronously every render so onNewColumnsLoaded (which fires
  // inside ag-grid's useEffect, before ours) always sees the latest column defs.
  const columnDefinitionsRef = useRef(columnDefinitions);
  columnDefinitionsRef.current = columnDefinitions;

  useEffect(() => {
    currentGridContext.current = {
      currentRowLensesId: columnFilterProps?.currentRowLensesId,
      dataUpdatedAt,
      expandedRows,
      expandedGroupKeys,
      usesExpandedGroupKeys,
      inEditMode,
      selectedRowGroupId: selectedRowGroup?.id,
      currentColumnOrder,
      currentColumnsHidden,
      dataTableError,
      report: props.report,
      columnValues: props.pivotColumns,
      columnValueGroupIds: props.pivotColumnGroupIds ?? null,
      canEditReport,
      reportConfigurationId,
      deletedRowGroupings: props.deletedRowGroupings ?? {},
      columnGroups,
      columnDefinitions,
      updatedAt,
      rollupDimensionDataUpdatedAt,
      multiHierarchyShowTotalsAtBottom,
      expandAllRunRef,
      lockRowExpansion
    };
  }, [
    columnFilterProps?.currentRowLensesId,
    expandedGroupKeys,
    expandedRows,
    usesExpandedGroupKeys,
    inEditMode,
    selectedRowGroup?.id,
    currentColumnOrder,
    currentColumnsHidden,
    dataTableError,
    props.pivotColumns,
    props.pivotColumnGroupIds,
    props.report,
    canEditReport,
    reportConfigurationId,
    props.deletedRowGroupings,
    columnGroups,
    columnDefinitions,
    updatedAt,
    rollupDimensionDataUpdatedAt,
    multiHierarchyShowTotalsAtBottom,
    lockRowExpansion,
    expandAllRunRef
  ]);

  // Trigger Refresh of Server Side Data.
  //
  // `isRefreshRowData` is set by the Refresh button, structure/calculation editors, and
  // anywhere else the user has indicated they want fresh data. Calling
  // `refreshServerSide({ purge: true })` alone is not enough: ag-grid drops its row buffer
  // and re-issues getRows, but the wretch cache middleware compares each entry's HTTP
  // `Date` header against `expireIfOlderThan` and refuses to evict an entry served later
  // than the freshness signal. That's the case for any same-session prefetch /
  // initial-load response, and for COA-rollup edits whose `dimensionSchema.dataUpdatedAt`
  // doesn't always advance promptly. Drop the pivot cache namespace first so the next
  // network round-trip is unconditional.
  //
  // The one exception is `isRefreshRowKeepingCache`, set when columns finish loading as a report
  // opens. Nothing there is stale and the cache holds the rows the prefetch just fetched, so the
  // grid only purges its own rows.
  useLayoutEffect(() => {
    if (!gridAPI) return;
    if (!isRefreshRowData) return;
    if (isRefreshRowKeepingCache) {
      gridAPI.refreshServerSide({ purge: true });
      return;
    }
    void clearPivotRowAndColumnCache().finally(() => {
      gridAPI.refreshServerSide({ purge: true });
    });
    // `isRefreshRowKeepingCache` is a dependency too: a clearing refresh that arrives while a keep-cache
    // one is still latched leaves `isRefreshRowData` true and only flips this flag, and it must still run.
  }, [gridAPI, isRefreshRowData, isRefreshRowKeepingCache]);

  // For multi-hierarchy reports, the rollup dimension's `dataUpdatedAt` is fetched
  // asynchronously via `useRollupDimensionFreshness`. On soft navigation react-query
  // serves the previously-cached freshness immediately while it revalidates in the
  // background, so the first getRows / prefetch can prime the cache with a stale
  // `expireIfOlderThan`. The cache middleware compares cached entries' HTTP `Date`
  // header against `expireIfOlderThan`, but a same-session prefetch produces an entry
  // with `Date > rollupDataUpdatedAt`, which cannot be invalidated by the timestamp
  // alone. Drop the namespace and refresh ag-grid whenever the rollup timestamp
  // advances after the first sync so the next fetch goes to the network.
  const previousRollupDimensionDataUpdatedAt = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!gridAPI) return;
    if (previousRollupDimensionDataUpdatedAt.current === rollupDimensionDataUpdatedAt) return;
    const isFirstSync = previousRollupDimensionDataUpdatedAt.current === undefined;
    previousRollupDimensionDataUpdatedAt.current = rollupDimensionDataUpdatedAt;
    if (isFirstSync) return;
    void clearPivotRowAndColumnCache().finally(() => {
      gridAPI.refreshServerSide({ purge: true });
    });
  }, [gridAPI, rollupDimensionDataUpdatedAt]);

  const gridOptions = useMemo((): GridOptions => {
    const topLevelRowClass = (styles as Record<string, string>).topLevelRow;
    const totalRowClass = (styles as Record<string, string>).totalRow;
    const nonCoaTotalRowClass = (styles as Record<string, string>).nonCoaTotalRow;
    const leftmostMinWidth =
      !!props.report && isMultiHierarchyAccountSummary(props.report) && multiHierarchyShowTotalsAtBottom
        ? AG_GRID_MIN_LEFTMOST_COLUMN_WIDTH_WITH_MH_TOTALS_CHECKBOX
        : AG_GRID_MIN_LEFTMOST_COLUMN_WIDTH;
    const hideColumnGroupHeaders =
      !inEditMode && (props.report?.hideColumnGroupHeaders || areAllColumnGroupHeadersHidden(columnGroups));
    return {
      allowContextMenuWithControlKey: false,
      maintainColumnOrder: true,
      autoGroupColumnDef: {
        pinned: 'left' as const,
        headerName: 'Row Label',
        ...(hideColumnGroupHeaders ? {} : { headerComponent: 'AutoGroupHeaderComponent' }),
        suppressSpanHeaderHeight: true,
        field: 'row_group_1',
        minWidth: leftmostMinWidth,
        cellRenderer: 'agGroupCellRenderer',
        valueGetter: (params) => {
          if (!params.data) return '';
          return params.data[`row_group_${String(params.node?.level)}`] ?? '';
        },
        cellRendererSelector: (params) => {
          if (params.data?.groupData?.type === EGroupType.Expression) {
            return {
              component: 'agGroupCellRenderer',
              params: { ...params, suppressDoubleClickExpand: true, suppressEnterExpand: true, canExpand: false }
            };
          }
          // Leaf rows (canExpand === false) must not be expandable via double-click /
          // Enter. Without this, AG Grid's default group-cell behavior still toggles
          // expansion for leaves, which in multi-hierarchy reports triggers a child
          // fetch that returns the row's own GROUPING aggregate as a "{label} Totals"
          // row, appearing to duplicate on every keystroke (BUD-16654).
          const leafRowData = params.data as { canExpand?: boolean } | undefined;
          if (leafRowData?.canExpand === false) {
            return {
              component: 'agGroupCellRenderer',
              params: { ...params, suppressDoubleClickExpand: true, suppressEnterExpand: true }
            };
          }
          return cellRendererSelector(params);
        },
        cellRendererParams: {
          suppressCount: true,
          onEditClick: events.onEditRowGroup,
          innerRenderer: MdsRowGroupHeader,
          expandCollapseOneLevelEnabled,
          localeText,
          suppressDoubleClickExpand: lockRowExpansion,
          suppressEnterExpand: lockRowExpansion
        }
      },
      rowClassRules: {
        [styles.hideExpandIcon]: function (params) {
          if (lockRowExpansion) return true;
          const isLeafNode = params.data ? !params.data?.canExpand : false;
          return isLeafNode;
        },
        [topLevelRowClass]: (params: RowClassParams<{ isTopLevel: boolean }>) => !!params.data?.isTopLevel,
        // CoA totals rows get the standard totalRow class; non-CoA synthesized totals rows
        // set isNonCoaTotalRow alongside isTotalRow so they can be styled distinctly.
        [totalRowClass]: (params: RowClassParams<{ isTotalRow: boolean; isNonCoaTotalRow: boolean }>) =>
          !!params.data?.isTotalRow && !params.data.isNonCoaTotalRow,
        [nonCoaTotalRowClass]: (params: RowClassParams<{ isNonCoaTotalRow: boolean }>) =>
          !!params.data?.isNonCoaTotalRow,
        ...(Object.fromEntries(
          Array(7)
            .fill(0)
            .map((_, i) => [
              styles[`tmpIndentation${String(i + 1)}`],
              (params: RowClassParams) => params.node.level === i + 1
            ])
        ) as Record<string, (params: RowClassParams) => boolean>)
      },
      // Injected root-level breakdown rows sit at ag-grid level 0 and the rows fetched beneath one
      // restart at level 1, so an ag-grid level understates the real report depth of the whole
      // subtree by the injected row's configured breakdown level. Publish that shortfall as an
      // inherited custom property and let FinancialReport.module.scss multiply it by the grid's own
      // indent step. A custom property rather than a class ladder keeps the offset unbounded, which
      // is what the CSV and PDF exports do, and keeps it in lock-step with the indent token.
      getRowStyle: (params: RowClassParams<{ breakdownIndentLevels?: number }>) => {
        const indentSteps = params.data?.breakdownIndentLevels;
        if (!indentSteps) return undefined;
        return { '--mh-breakdown-indent-steps': String(indentSteps) };
      },
      context: {
        localeText,
        ...events,
        get: () => currentGridContext.current
      },
      defaultColDef: {
        cellRendererSelector: undefined,
        resizable: true,
        sortable: false,
        suppressHeaderMenuButton: true,
        tooltipComponentParams: { isDetailTable: false },
        suppressKeyboardEvent,
        suppressHeaderKeyboardEvent: suppressKeyboardEvent
      },
      enableRangeSelection: true,
      components: {
        AutoGroupHeaderComponent,
        DeletedGroupColumnHeader,
        MdsColumnHeader,
        CustomLoadingCell,
        NoRowsOverlay,
        PendingValueCell,
        ZeroRowsStatusBar,
        ...(expandCollapseOneLevelEnabled
          ? { agColumnHeader: DefaultColumnGroupHeader, agColumnGroupHeader: DefaultColumnGroupHeader }
          : {})
      },
      noRowsOverlayComponent: 'NoRowsOverlay',
      getRowId: (row) => row.data.id,
      loadingCellRenderer: 'CustomLoadingCell',
      onModelUpdated: events.onModelUpdated,
      onRowGroupOpened: events.onRowGroupOpened,

      onDragStopped(event: DragStoppedEvent) {
        if (!columnMoved.current) return;
        columnMoved.current = false;
        const columnState = event.api.getColumnState().map((column) => {
          column.colId = normalizeAgGridKey(column.colId);
          return column;
        });

        //if the locked group column was moved to the right, put it back
        const lockedIndex = columnState.findIndex((column) => column.colId === AG_GRID_GROUP_COLUMN_ID);
        if (lockedIndex > 0) {
          const [column] = columnState.splice(lockedIndex, 1);
          columnState.unshift(column);
        }
        const newColumnOrder = Object.fromEntries(columnState.map((column, index) => [column.colId, index]));
        events.onColumnOrderChange?.(newColumnOrder);
      },
      onColumnMoved() {
        columnMoved.current = true;
      },
      onNewColumnsLoaded(event) {
        // Definition order (not the grid's display state) so reorderGroupInState and stale-group layout
        // place new fiscal-year columns in chronological order.
        const defOrderIds = getDefinitionOrderColIds(event.api, columnDefinitionsRef.current);
        const prevCountByPrefix = prevCountByPrefixRef.current;
        prevCountByPrefixRef.current = buildCountByPrefix(defOrderIds);

        const currentColumnOrder = currentGridContext.current?.currentColumnOrder ?? {};

        if (prevCountByPrefix === null) {
          applyColumnOrder(event.api, currentColumnOrder, defOrderIds);
        } else {
          applyReorderedColumns(event.api, currentColumnOrder, defOrderIds, prevCountByPrefix, events);
        }

        const currentColumnsHidden = currentGridContext.current?.currentColumnsHidden;
        if (currentColumnsHidden) {
          applyColumnsVisibility(event.api, currentColumnsHidden);
        }
      },
      statusBar: {
        statusPanels: [
          {
            statusPanel: 'ZeroRowsStatusBar',
            key: 'ZeroRowsStatusBar',
            statusPanelParams: { setterRef: zeroRowsSetterRef }
          }
        ]
      },
      // ag-grid v34 reads --ag-header-height CSS variable (default 48px) for layout rather than
      // the balham theme's CSS height:50px, reducing visible area for wrapped header text.
      // Explicitly setting these restores the pre-upgrade header height behaviour.
      headerHeight: 50,
      groupHeaderHeight: 50,
      cacheBlockSize: 1000,
      rowModelType: 'serverSide' as const,
      rowSelection: 'single' as const,
      serverSideDatasource,
      suppressMultiRangeSelection: true,
      suppressContextMenu: true,
      suppressColumnMoveAnimation: true,
      blockLoadDebounceMillis: props.rowGroupDebounceMs
    };
  }, [
    serverSideDatasource,
    props.pivotColumns,
    props.report,
    usesExpandedGroupKeys,
    props.rowGroupDebounceMs,
    multiHierarchyShowTotalsAtBottom,
    columnGroups,
    inEditMode,
    expandCollapseOneLevelEnabled,
    lockRowExpansion,
    events,
    localeText,
    suppressKeyboardEvent
  ]);

  const handleGridReady = useCallback((params: GridReadyEvent) => {
    setGridAPI(params.api);
    events.onGridReady?.(params);
  }, []);

  useRefreshOnModeChange(gridAPI, inEditMode);

  let suppressZerosInRows = false;
  if (props.report && props.report.formatting && props.report.formatting.zeroNumbers) {
    suppressZerosInRows = props.report.formatting.zeroNumbers[0].enabled;
  }
  useShowNoRowsOverlay(gridAPI, suppressZerosInRows, hasParentData);
  useZeroRowsStatusBar({
    setterRef: zeroRowsSetterRef,
    inEditMode,
    canEditReport,
    suppressZerosInRows,
    localeText,
    onClickEditReport,
    setShowFormattingModal,
    hasParentData
  });

  return {
    gridOptions,
    gridAPI,
    onGridReady: handleGridReady
  };
}

function useRefreshOnModeChange(gridAPI: GridApi | null, inEditMode: boolean) {
  // Updates on inEditMode change
  useEffect(() => {
    if (!gridAPI) return;
    // trigger re-render of header components
    // Use rAf to work within ag-grid's lifecycle
    requestAnimationFrame(() => {
      gridAPI.redrawRows();
      gridAPI.refreshHeader();
      setTimeout(() => {
        gridAPI.autoSizeAllColumns();
      }, 100);
      gridAPI.clearRangeSelection();
      gridAPI.deselectAll();
    });
  }, [gridAPI, inEditMode]);
}

function useShowNoRowsOverlay(gridApi: GridApi | null, suppressZerosInRows: boolean, hasParentData: boolean | null) {
  useEffect(() => {
    if (!gridApi || hasParentData === null) return;

    if (suppressZerosInRows && !hasParentData) {
      gridApi.showNoRowsOverlay();
    } else {
      gridApi.hideOverlay();
    }
  }, [gridApi, suppressZerosInRows, hasParentData]);
}

export function useShowRowPlaceholder(gridAPI: GridApi | null, showRowPlaceHolder: boolean) {
  // Show/Hide row placeholder on showRowPlaceHolder change
  useEffect(() => {
    if (!gridAPI) return;
    const placeholderRow = gridAPI.getRowNode(AG_GRID_NEW_ROW_PLACEHOLDER_ID);
    if (!placeholderRow) return;
    // If we should show the row but rowHeight is 0, set it to non-zero
    // Else do the other way
    if (showRowPlaceHolder && placeholderRow.rowHeight === 0) {
      // Check if there is any parent row remained selected
      if (gridAPI.getSelectedNodes().length > 0) {
        gridAPI.forEachNode((row) => {
          row.setSelected(false);
        });
      }
      placeholderRow.setRowHeight(AG_GRID_DEFAULT_ROW_HEIGHT);
      gridAPI.ensureNodeVisible(placeholderRow);
      gridAPI.onRowHeightChanged();
      selectNodeAndChildren(gridAPI, placeholderRow);
    } else if (!showRowPlaceHolder && placeholderRow.rowHeight !== 0) {
      placeholderRow.setRowHeight(0);
      placeholderRow.setSelected(false);
      gridAPI.onRowHeightChanged();
    }
  }, [gridAPI, showRowPlaceHolder]);
}

export function useShowColumnPlaceholder(gridApi: GridApi | null, showColumnPlaceholder: boolean) {
  // Show/Hide column placeholder on showColumnPlaceHolder change
  useEffect(() => {
    if (!gridApi) return;
    const placeholderColumn = gridApi.getColumn(AG_GRID_NEW_COLUMN_PLACEHOLDER_ID);
    if (!placeholderColumn) return;
    if (placeholderColumn.isVisible() !== showColumnPlaceholder) {
      gridApi.moveColumns([AG_GRID_NEW_COLUMN_PLACEHOLDER_ID], gridApi.getColumns()?.length ?? 0);
      gridApi.setColumnsVisible([AG_GRID_NEW_COLUMN_PLACEHOLDER_ID], showColumnPlaceholder);
      if (showColumnPlaceholder) {
        gridApi.ensureColumnVisible(AG_GRID_NEW_COLUMN_PLACEHOLDER_ID);

        // select only placeholder column
        gridApi.clearRangeSelection();
        gridApi.deselectAll();
        gridApi.addCellRange({
          columns: [placeholderColumn],
          rowStartIndex: 0,
          rowEndIndex: Infinity
        });
      }
    }
  }, [gridApi, showColumnPlaceholder]);
}

const cellRendererSelector = (params: ICellRendererParams): CellRendererSelectorResult | undefined => {
  const { valueFormatted } = params;

  if (!valueFormatted || typeof valueFormatted === 'number') return undefined;

  if (valueFormatted in CellErrorTypes) {
    return {
      component: 'IconCellRenderer',
      params: { icon: 'status-warning', text: cellErrorTypeText[valueFormatted as CellErrorTypes] }
    };
  }

  if (valueFormatted.endsWith('...')) {
    return { component: 'IconCellRenderer', params: { icon: 'status-info', text: valueFormatted } };
  }

  return undefined;
};

const GRID_CELL_CLASSNAME = 'ag-cell';
const GRID_HEADER_CELL_CLASSNAME = 'ag-header-cell';
const GRID_HEADER_GROUP_CELL_CLASSNAME = 'ag-header-group-cell';

function getAllFocusableElementsOf(el: HTMLElement) {
  return Array.from<HTMLElement>(
    el.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
  ).filter((focusableEl) => focusableEl.tabIndex !== -1);
}

function getEventPath(target: HTMLElement): HTMLElement[] {
  const path: HTMLElement[] = [];
  let currentTarget: HTMLElement | null = target;
  while (currentTarget) {
    path.push(currentTarget);
    currentTarget = currentTarget.parentElement ?? null;
  }
  return path;
}

/**
 * Capture whether the user is tabbing forwards or backwards and suppress keyboard event if tabbing
 * outside of the children
 */
export function suppressKeyboardEvent({ event }: { event: KeyboardEvent }) {
  const { key, shiftKey } = event;
  const path = getEventPath(event.target as HTMLElement);
  const activeElement = document.activeElement as HTMLElement;
  const isTabForward = key === 'Tab' && !shiftKey;
  const isTabBackward = key === 'Tab' && shiftKey;
  let suppressEvent = false;

  if (isTabForward || isTabBackward) {
    const eGridCell = path.find(
      (el) =>
        el.classList.contains(GRID_CELL_CLASSNAME) ||
        el.classList.contains(GRID_HEADER_CELL_CLASSNAME) ||
        el.classList.contains(GRID_HEADER_GROUP_CELL_CLASSNAME)
    );
    if (!eGridCell) {
      return suppressEvent;
    }

    const focusableChildrenElements = getAllFocusableElementsOf(eGridCell);
    const lastCellChildEl = focusableChildrenElements[focusableChildrenElements.length - 1];
    const firstCellChildEl = focusableChildrenElements[0];
    // Suppress keyboard event if tabbing forward within the cell and the current focused element is not the last child
    if (focusableChildrenElements.length === 0) {
      return false;
    }

    const currentIndex = focusableChildrenElements.indexOf(activeElement);
    if (isTabForward) {
      if (activeElement !== lastCellChildEl) {
        suppressEvent = true;
        if (currentIndex !== -1 || activeElement === eGridCell) {
          event.preventDefault();
          focusableChildrenElements[currentIndex + 1].focus();
        }
      }
      // Suppress keyboard event if tabbing backwards within the cell, and the current focused element is not the first child
    } else {
      const cellHasFocusedChildren = eGridCell.contains(activeElement) && eGridCell !== activeElement;
      // Manually set focus to the last child element if cell doesn't have focused children
      if (!cellHasFocusedChildren) {
        lastCellChildEl.focus();
        event.preventDefault();
      }

      if (activeElement !== firstCellChildEl) {
        suppressEvent = true;
        if (currentIndex !== -1) {
          event.preventDefault();
          focusableChildrenElements[currentIndex - 1].focus();
        } else if (activeElement === eGridCell) {
          event.preventDefault();
          lastCellChildEl.focus();
        }
      }
    }
  }

  return suppressEvent;
}
